import type { Customer, Pricing, PriceBreakdown, PaymentKeys, PaymentMethod } from './checkout';
import type { ChargebeePaymentIntent } from './chargebee';

/**
 * Subscription checkout types, ported from independent-checkout-v2
 * `contracts.ts` — only the members the v3 subscription flow consumes.
 * Served by the (un-versioned) `/checkout/subscriptions*` backend routes.
 */

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  EXPIRED = 'expired',
  PAUSED = 'paused',
  TRIAL = 'trial',
  PENDING = 'pending',
  FAILED_CHARGED = 'failed_charged',
}

export enum FinalizeSubscriptionStatus {
  SUCCESS = 'success',
  FAIL = 'fail',
}

export enum BillingFrequency {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export enum BillingCycleType {
  ONGOING = 'ongoing',
  FIXED = 'fixed',
}

export interface PlanSupportedCurrency {
  currency: string;
  itemPriceId: string;
}

export interface Plan {
  _id: string;
  publisherId: string;
  /** Chargebee item id (Appcharge Mongo id, e.g. `68b6...`). */
  providerPlanId: string;
  providerPlanName: string;
  displayName: string;
  name: string;
  /** Chargebee item price id (e.g. `68b6...-USD-p`). */
  itemPriceId: string;
  billingFrequency: BillingFrequency;
  billingCycleType: BillingCycleType;
  description: string;
  billingCycles: number;
  basePriceInUsd: number;
  supportedCurrencies: PlanSupportedCurrency[];
}

export interface ChargebeePK {
  siteId: string;
  publicKey: string;
}

/** `pks` on the subscription summary additionally carries the Chargebee keys. */
export interface SubscriptionPaymentKeys extends PaymentKeys {
  chargebee: ChargebeePK;
}

export interface FirstPaymentDiscount {
  priceBreakdown: PriceBreakdown;
}

export interface SubscriptionsSummary {
  customer: Customer;
  pricing: Pricing;
  /**
   * Full Chargebee payment_intent created by the backend. Passed VERBATIM to
   * `authorizeWith3ds()` / wallet `setPaymentIntent()` — treat as opaque
   * beyond `id`.
   */
  paymentIntent: ChargebeePaymentIntent;
  firstPaymentDiscount?: FirstPaymentDiscount;
  plan: Plan;
  pks: SubscriptionPaymentKeys;
  redirectUrl?: string;
  /**
   * APMs the gateway routed for this plan's currency/country — the routed
   * payment-method records flagged `isSupportSubscription`, same shape as
   * `/checkout/v3`'s list. Card / Google Pay / Apple Pay are NOT in it (those
   * stay FE-owned, see `buildSubscriptionPaymentMethods`). Absent on older
   * gateways.
   */
  paymentMethods?: PaymentMethod[];
}

export interface FinalizeSubscriptionRequest {
  params: { planId: string };
  customer: { id: string; email?: string };
}

export interface FinalizeSubscriptionResponse {
  status: FinalizeSubscriptionStatus;
  subscriptionId: string;
}

export interface GetSubscriptionResponse {
  subscriptionId: string;
  status: SubscriptionStatus;
}
